import { ApiResult, request, safeRequest } from '@synergy/api';
import { BonusConditionValue, BonusSettlementStatus, CustomerBonusSettlement, Media } from '@synergy/models';

import { BonusSettlementSupplierDetail } from '../models/bonus-settlement-supplier-detail';
import { BonusSettlementSupplierDetailEditFormValues } from '../models/bonus-settlement-supplier-detail-edit-form-values';

interface SaveRequestCustomerBonusSettlement {
    id: number;
    isFinished: boolean;
    bonusConditionValues: BonusConditionValue[];
    noteToCustomer?: string;
    isReclaimed: boolean;
}
export interface SaveBonusSettlementSupplierDetailParams {
    customerBonusSettlements: SaveRequestCustomerBonusSettlement[];
}

export class BonusSettlementsSupplierDetailApi {
    constructor(protected baseUrl: string) {}

    public getBonusSettlementSupplierDetail(
        id: number,
        supplierBonusSettlementId: number
    ): Promise<ApiResult<BonusSettlementSupplierDetail>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/details`),
            response => response.data
        );
    }

    public updateSingleNoteToCustomer(
        id: number,
        supplierBonusSettlementId: number,
        customerBonusSettlementId: number,
        data: Omit<BonusSettlementSupplierDetailEditFormValues, 'id'>
    ): Promise<ApiResult<null>> {
        return safeRequest(
            () =>
                request.post(
                    `${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/customers/${customerBonusSettlementId}/update-pool-bonus`,
                    data
                ),
            () => null
        );
    }

    public updateAllNoteToCustomer(
        id: number,
        supplierBonusSettlementId: number,
        noteToCustomer: string
    ): Promise<ApiResult<BonusSettlementSupplierDetail>> {
        return safeRequest(
            () =>
                request.post(
                    `${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/update-note-to-customers`,
                    { noteToCustomer }
                ),
            response => response.data
        );
    }

    public saveBonusSettlementSupplierDetail(
        id: number,
        supplierBonusSettlementId: number,
        data: SaveBonusSettlementSupplierDetailParams
    ): Promise<ApiResult<BonusSettlementSupplierDetail>> {
        return safeRequest(
            () =>
                request.post(
                    `${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/save`,
                    data
                ),
            response => response.data
        );
    }

    public submitBonusSettlementSupplierDetail(
        id: number,
        supplierBonusSettlementId: number
    ): Promise<ApiResult<BonusSettlementStatus>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/submit`),
            response => response.data
        );
    }

    public updateNoteFromSupplier(
        id: number,
        supplierBonusSettlementId: number,
        noteFromSupplier: string
    ): Promise<ApiResult<null>> {
        return safeRequest(
            () =>
                request.post(
                    `${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/update-note-from-supplier`,
                    { noteFromSupplier }
                ),
            () => null
        );
    }

    public updateInternalNote(
        id: number,
        supplierBonusSettlementId: number,
        internalNote: string
    ): Promise<ApiResult<null>> {
        return safeRequest(
            () =>
                request.post(
                    `${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/update-internal-note`,
                    { internalNote }
                ),
            () => null
        );
    }

    public uploadFile(id: number, supplierBonusSettlementId: number, file: File): Promise<ApiResult<Media>> {
        return safeRequest(
            () => {
                const data = new FormData();
                data.append('file', file, file.name);

                const config = { headers: { 'content-type': 'multipart/form-data' } };

                return request.post(
                    `${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/upload-file`,
                    data,
                    config
                );
            },
            response => response.data
        );
    }

    public deleteFiles(
        id: number,
        supplierBonusSettlementId: number,
        deletedFileIds: number[]
    ): Promise<ApiResult<null>> {
        return safeRequest(
            () =>
                request.delete(
                    `${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/remove-files`,
                    { data: { documentIds: deletedFileIds } }
                ),
            () => null
        );
    }

    public reclaimCustomer(
        id: number,
        supplierBonusSettlementId: number,
        customerBonusSettlementId: number
    ): Promise<ApiResult<null>> {
        return safeRequest(
            () =>
                request.post(
                    `${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/customers/${customerBonusSettlementId}/reclaim`
                ),
            () => null
        );
    }

    public revertReclaimCustomer(
        id: number,
        supplierBonusSettlementId: number,
        customerBonusSettlementId: number
    ): Promise<ApiResult<null>> {
        return safeRequest(
            () =>
                request.post(
                    `${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/customers/${customerBonusSettlementId}/revert-reclaim`
                ),
            () => null
        );
    }

    public addCustomer(
        id: number,
        supplierBonusSettlementId: number,
        customerId: number
    ): Promise<ApiResult<CustomerBonusSettlement>> {
        return safeRequest(
            () =>
                request.post(
                    `${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/add-customer`,
                    { customerId }
                ),
            response => response.data
        );
    }

    public deleteCustomer(
        id: number,
        supplierBonusSettlementId: number,
        customerBonusSettlementId: number
    ): Promise<ApiResult<null>> {
        return safeRequest(
            () =>
                request.delete(
                    `${this.baseUrl}/bonus-settlements/${id}/suppliers/${supplierBonusSettlementId}/customers/${customerBonusSettlementId}/remove`
                ),
            () => null
        );
    }
}
